import { Injectable } from '@angular/core';
import { createStore, select, setProp, withProps } from '@ngneat/elf';
import {
  withEntities,
  selectAllEntities,
  setEntities,
  getEntity,
  addEntities,
  updateEntities,
  deleteEntities,
} from '@ngneat/elf-entities';
import { persistState } from '@ngneat/elf-persist-state';
import * as localforage from 'localforage';
import * as moment from 'moment';
import { REFRESH_TIME_IN_MINUTES } from 'src/app/shared/configs/global-values.constants';
import { ApiBaseService } from 'src/app/shared/services/api/api-base/api-base.service';
import {
  IStoreProps,
  IListParams,
  IDeleteParams,
  IPaginationState,
  IScrollPosition,
} from 'src/app/talent-litmus-core/common/common.interface';
import { IWheelSpinGame } from './wheel-spin-game.interface';
import { Observable, map, tap } from 'rxjs';
import { PageEvent } from '@angular/material/paginator';

// eslint-disable-next-line @typescript-eslint/naming-convention
const STORE_NAME = 'lvl3-wheel-spin-game';

const instance = localforage.createInstance({
  driver: localforage.INDEXEDDB,
  name: 'talent-litmus-adminv2',
  version: 1.0,
  storeName: STORE_NAME,
});

const store = createStore(
  { name: STORE_NAME },
  withProps<IStoreProps>({ lastRefreshed: undefined }),
  withProps<IPaginationState>({
    length: 0,
    pageIndex: 0,
    pageSize: 0,
    previousPageIndex: 0,
  }),
  withEntities<IWheelSpinGame, '_id'>({ idKey: '_id' }),
  withProps<IScrollPosition>({ x: 0, y: 0 }),
  withProps<{ selectedId: string }>({ selectedId: null }),
  withProps<{ searchParam: string }>({ searchParam: '' }),
);

export const persist = persistState(store, {
  key: STORE_NAME,
  storage: instance,
});

@Injectable()
export class WheelSpinGameCoreService {
  private _loading = false;

  /**
   * @name wheelSpinGameList$
   * @description emits stored list of the companies
   */

  wheelSpinGameList$ = store.pipe(selectAllEntities());
  paginationState$ = store.pipe(
    select((state) => {
      return state;
    }),
  );
  totalCount: number;

  public get loading(): boolean {
    return this._loading;
  }

  constructor(private _api: ApiBaseService) {
    if (!store.value.lastRefreshed) {
      setTimeout(() => {
        this.refresh();
      }, 400);
    } else {
      this.refresh();
    }
  }

  public getWheelSpinGames(params?: IListParams): void {
    this._loading = true;
    this._api
      .get(`wheel-spin-game/list`, params || {})
      .subscribe((data: IWheelSpinGameListResponse) => {
        this.totalCount = data.totalCount;
        store.update(
          setEntities(data.list),
          setProp('lastRefreshed', new Date()),
        );
        this._loading = false;
      });
  }

  public addWheelSpinGame(dataInput): Observable<IWheelSpinGame> {
    this._loading = true;
    return this._api.post('wheel-spin-game/add', dataInput || {}).pipe(
      tap((data: IWheelSpinGame) => {
        store.update(addEntities(data));
        this._loading = false;
      }),
    );
  }

  public updateWheelSpinGame(dataInput): Observable<IWheelSpinGame> {
    this._loading = true;
    return this._api.put('wheel-spin-game/edit', dataInput || {}).pipe(
      map((data: ArrayBuffer) => data as any),
      tap((data: IWheelSpinGame) => {
        store.update(updateEntities(data['_id'], data));
        this._loading = false;
      }),
    );
  }

  public deleteWheelSpinGame(dataInput: IDeleteParams): Observable<any> {
    this._loading = true;
    return this._api.delete('wheel-spin-game', { body: dataInput }).pipe(
      tap(() => {
        store.update(deleteEntities(dataInput['_id']));
        this._loading = false;
      }),
    );
  }

  public getWheelSpinGameInfo(id: string): IWheelSpinGame {
    const wheelSpinGame = store.query(getEntity(id));
    return wheelSpinGame;
  }

  public resolveWheelSpinGameId(
    wheelSpinGame: string | IWheelSpinGame,
  ): string {
    let wheelSpinGameId: string | IWheelSpinGame = '';
    if (wheelSpinGame) {
      wheelSpinGameId = wheelSpinGame;
    }
    if (wheelSpinGame['_id']) {
      wheelSpinGameId = wheelSpinGame['_id'];
    }
    return wheelSpinGameId as string;
  }

  moveToLastPage(): void {
    let length: number;
    let pageSize: number;
    let previousPageIndex: number;
    this.paginationState$.subscribe((state) => {
      length = state.length;
      pageSize = state.pageSize;
      previousPageIndex = state.previousPageIndex;
    });
    this.pageChangeEvent({
      length,
      pageIndex: Math.ceil(length / pageSize),
      pageSize,
      previousPageIndex,
    });
  }

  pageChangeEvent(event: PageEvent): void {
    store.update((state) => ({
      ...state,
      length: event.length,
      pageIndex: event.pageIndex,
      pageSize: event.pageSize,
      previousPageIndex: event.previousPageIndex,
    }));
  }

  setScrollPosition(scrollPostion: [number, number]): void {
    store.update((state) => ({
      ...state,
      x: scrollPostion[0],
      y: scrollPostion[1],
    }));
  }

  setSelectedId(id: string): void {
    store.update((state) => ({
      ...state,
      selectedId: id,
    }));
  }

  setSearchParam(searchParam: string): void {
    store.update((state) => ({
      ...state,
      searchParam: searchParam,
    }));
  }

  public refresh(forced?: boolean): void {
    if (
      forced ||
      !store.value.lastRefreshed ||
      moment().diff(moment(store.value.lastRefreshed), 'minutes') >
        REFRESH_TIME_IN_MINUTES
    ) {
      this.getWheelSpinGames();
    }
  }
}

interface IWheelSpinGameListResponse {
  list: IWheelSpinGame[];
  totalCount: number;
}
